/**
 * Puente de sincronización entre el widget de divisas (Nova: Multi Currency
 * Converter) y los mercados de Shopify.
 *
 * Problema que resuelve: al elegir en el widget una divisa que corresponde a un
 * mercado real (CZK, DKK, HUF, PLN, RON, CHF, SEK...), la app fija su cookie
 * `cart_currency` y recarga, pero NO cambia el país (`localization`). Shopify
 * sigue sirviendo el mercado base en EUR y el usuario tiene que seleccionar la
 * divisa una segunda vez.
 *
 * Estrategia (cooperativa, no bloqueante):
 *  1. Escuchar la elección de divisa del widget (select nativo y dropdown) SIN
 *     detener su flujo: Nova necesita procesar el evento para registrar la
 *     divisa en su propio almacenamiento; si se le bloquea, en la siguiente
 *     carga "restaura" la divisa anterior y deshace el cambio del usuario.
 *  2. Persistir la preferencia en localStorage ANTES de cualquier recarga.
 *  3. Si la divisa pertenece a un mercado real, lanzar en paralelo el POST a
 *     `/localization` con el país correcto usando `keepalive` (sobrevive a la
 *     navegación que Nova dispara): cuando su recarga llega al servidor, el
 *     país ya es el correcto y región+divisa cambian en un solo paso. Las
 *     divisas solo-app (USD, GBP, CAD, BGN) elegidas desde un mercado real
 *     anclan el mercado base por la misma vía: Nova solo sabe pintarlas sobre
 *     la divisa base, así que su recarga debe aterrizar en EUR.
 *  4. Al cargar cada página, comprobar que el mercado activo coincide con la
 *     preferencia guardada; si la recarga aterrizó en EUR porque perdimos la
 *     carrera, reaplicar UNA sola vez (guard en sessionStorage: sin bucles).
 *
 * El mapa divisa→país se genera en Liquid desde `localization.available_countries`
 * (ver snippets/currency-sync.liquid), así que refleja siempre los mercados
 * realmente configurados en el admin.
 */
(function () {
  'use strict';

  var STORAGE_KEY = 'huiike:market-preference';
  var RETRY_KEY = 'huiike:market-retry';

  /* ---------- Mapa divisa → país (inyectado por Liquid) ---------- */

  var marketMap = {};
  var countryCurrency = {};
  var baseCurrency = null;
  var languageCountry = {};
  var languageDisplay = {};

  function buildMaps() {
    var node = document.querySelector('[data-market-currency-map]');
    if (!node) return;

    var data;
    try {
      data = JSON.parse(node.textContent);
    } catch (error) {
      return;
    }

    baseCurrency = data.base || null;
    languageCountry = data.languages || {};
    languageDisplay = data.displayLanguages || {};

    (data.countries || []).forEach(function (pair) {
      if (!(pair.currency in marketMap)) marketMap[pair.currency] = pair.country;
      countryCurrency[pair.country] = pair.currency;
    });

    // Varios países comparten la divisa base: al volver a ella se prefiere
    // España, el país base de la tienda, si está disponible.
    if (countryCurrency.ES) marketMap[countryCurrency.ES] = 'ES';
  }

  /* ---------- Persistencia (localStorage con fallback silencioso) ---------- */

  function persistPreference(currency, country) {
    try {
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ currency: currency, country: country }));
    } catch (error) {
      /* almacenamiento no disponible: el flujo sigue funcionando sin persistencia */
    }
  }

  function readPreference() {
    try {
      return JSON.parse(window.localStorage.getItem(STORAGE_KEY));
    } catch (error) {
      return null;
    }
  }

  function clearPreference() {
    try {
      window.localStorage.removeItem(STORAGE_KEY);
    } catch (error) {
      /* nada */
    }
  }

  // El guard de reintento SOLO se limpia aquí, cuando hay una elección
  // explícita del usuario. Limpiarlo al aterrizar con éxito en la divisa
  // preferida rearmaba el rescate automático en cada ciclo: cualquier actor
  // que devolviera la página a EUR (Nova restaurando su divisa guardada, una
  // app de geolocalización, el antiguo script de divisa por URL) provocaba
  // rescate → éxito → guard limpio → vuelta a EUR → rescate…, es decir, un
  // bucle infinito de recargas alternando región. Con el guard ligado a la
  // elección, hay como máximo UNA recarga automática por elección del usuario.
  function clearRetryGuard() {
    try {
      window.sessionStorage.removeItem(RETRY_KEY);
    } catch (error) {
      /* nada */
    }
  }

  /* ---------- Divisa de cortesía (sin mercado propio: solo pintura de Nova) ---------- */

  var DISPLAY_KEY = 'huiike:display-currency';

  function persistDisplayCurrency(currency) {
    try {
      window.localStorage.setItem(DISPLAY_KEY, currency);
    } catch (error) {
      /* nada */
    }
  }

  function readDisplayCurrency() {
    try {
      return window.localStorage.getItem(DISPLAY_KEY);
    } catch (error) {
      return null;
    }
  }

  function clearDisplayCurrency() {
    try {
      window.localStorage.removeItem(DISPLAY_KEY);
    } catch (error) {
      /* nada */
    }
  }

  // Sincroniza el botón del widget de Nova con el estado real de la página:
  // la divisa de cortesía si la hay (p. ej. GBP al navegar en inglés) o, en
  // mercados reales, la divisa del mercado activo — sin esto el widget se queda
  // mostrando su última divisa guardada (p. ej. "EUR" en el mercado polaco).
  // Nova no registra selecciones programáticas en su almacenamiento, así que
  // hay que relanzarlo en cada carga. Es una reescritura del DOM sin recarga
  // (verificado: en mercado real la conversión es identidad y el precio no
  // cambia), no puede provocar bucles. El retardo deja que Nova inicialice y
  // "restaure" su propia divisa antes de fijar la nuestra.
  function syncNovaWidget() {
    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    var display = readDisplayCurrency();

    var want = null;
    if (display && /^[A-Z]{3}$/.test(display) && active === baseCurrency) {
      want = display;
    } else if (active) {
      want = active;
    }
    if (!want) return;

    // Reflejar en el body la divisa que realmente se va a mostrar: las de
    // pintura (USD/GBP/CAD/BGN) no existen en el servidor, así que el
    // data-currency que emite Liquid siempre diría EUR y el CSS por divisa
    // (p. ej. el ancho del sidebar de filtros con BGN) nunca se activaría.
    if (document.body) document.body.dataset.currency = want;

    setTimeout(function () {
      var select = document.querySelector('#baCurrSelector');
      if (!select || select.value === want) return;
      if (!select.querySelector('option[value="' + want + '"]')) return;
      select.value = want;
      // Evento programático (no isTrusted): nuestros propios listeners lo ignoran.
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }, 2000);
  }

  /* ---------- Cambio de mercado ---------- */

  // `keepalive` mantiene vivo el POST aunque Nova navegue inmediatamente después:
  // cuando su recarga llega al servidor, el país correcto ya está fijado.
  function postLocalization(country) {
    var body = new FormData();
    body.append('form_type', 'localization');
    body.append('utf8', '✓');
    body.append('_method', 'put');
    body.append('country_code', country);
    body.append('return_to', window.location.pathname + window.location.search);

    return fetch('/localization', { method: 'POST', body: body, keepalive: true }).catch(function () {
      /* si el POST falla, reconcile() reintentará una vez en la próxima carga */
    });
  }

  var switching = false;

  function switchMarket(country) {
    if (switching) return;
    switching = true;

    postLocalization(country).then(function () {
      window.location.reload();
    });
  }

  /* ---------- Escucha del widget (cooperativa: nunca se bloquea a Nova) ---------- */

  function currentCountry() {
    return (window.Shopify && window.Shopify.country) || null;
  }

  function handleChoice(code) {
    if (!code || !/^[A-Z]{3}$/.test(code)) return;

    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    var country = marketMap[code];

    if (!country) {
      // Divisa solo-app (sin mercado propio): Nova solo sabe pintarla sobre la
      // divisa base, y el checkout seguirá en la divisa del mercado activo.
      persistDisplayCurrency(code); // que sobreviva a la siguiente carga
      clearRetryGuard();

      var home = marketMap[baseCurrency];
      if (active && active !== baseCurrency && home) {
        // Elegida desde un mercado real (p. ej. SEK): Nova recarga por su
        // cuenta sin cambiar el mercado, la recarga aterriza otra vez en ese
        // mercado y la pintura nunca llega a aplicarse (el usuario veía dos
        // recargas y la divisa sin cambiar). Única salida coherente: anclar el
        // mercado base y fijar el país en paralelo (keepalive), sin detener a
        // Nova — su recarga aterriza ya en EUR y syncNovaWidget pinta la
        // divisa elegida encima. switchMarket hace de red de seguridad por si
        // Nova no recargara (su navegación y la nuestra van a la misma URL,
        // así que como mucho se ve una recarga); si el POST pierde la carrera,
        // reconcile() remata con su único rescate en la siguiente carga.
        persistPreference(baseCurrency, home);
        switchMarket(home);
      } else if (active && currentCountry()) {
        // Ya en el mercado base: Nova la convierte en el DOM sin recargar. Se
        // ancla el mercado ACTUAL como preferencia (en vez de borrarla) para
        // que una cookie `localization` desviada —p. ej. país Bulgaria dentro
        // del mercado EUR— no sobreviva a la siguiente reconciliación. Como no
        // hay recarga, el CSS por divisa se activa reflejándola en el body.
        persistPreference(active, currentCountry());
        if (document.body) document.body.dataset.currency = code;
      } else {
        clearPreference();
      }
      return;
    }

    // Guardar SIEMPRE antes de que nada dispare una recarga. La elección
    // explícita concede un nuevo (y único) rescate automático. Un mercado real
    // sustituye a cualquier divisa de cortesía anterior.
    persistPreference(code, country);
    clearDisplayCurrency();
    clearRetryGuard();

    if (active === code) {
      // Mismo mercado, pero el país puede estar desviado dentro de él (varios
      // países comparten divisa): p. ej. usuario en BG/EUR que vuelve a elegir
      // EUR espera aterrizar en el país canónico (ES). Nova no recargará al no
      // cambiar la divisa, así que la recarga es nuestra. Si tampoco hace
      // falta recargar (país correcto), deshacer un posible reflejo anterior
      // de divisa de pintura en el body (p. ej. BGN→EUR sin recarga).
      if (document.body) document.body.dataset.currency = code;
      if (currentCountry() && currentCountry() !== country) switchMarket(country);
      return;
    }

    // Fijar el país y navegar nosotros tras el POST. No se detiene el evento
    // de Nova (debe registrar la divisa en su almacenamiento o la "restaura"
    // en la siguiente carga), pero NO se puede confiar en su recarga: desde el
    // mercado base Nova trata cualquier divisa como pintura y no recarga — el
    // país cambiaba en el servidor pero la página seguía renderizada en EUR
    // (p. ej. EUR→SEK: precios pintados en SEK con el mercado sueco sin
    // aplicar hasta la siguiente navegación). Si Nova también recarga, ambas
    // navegaciones van a la misma URL: como mucho se ve una recarga.
    switchMarket(country);
  }

  // Select nativo del widget. Solo eventos reales del usuario (`isTrusted`):
  // Nova dispara `change` programáticos al inicializar para "restaurar" su
  // divisa guardada; tratarlos como elecciones revertía la región del usuario
  // a EUR/español a los pocos segundos de cambiarla.
  document.addEventListener(
    'change',
    function (event) {
      if (!event.isTrusted) return;
      var target = event.target;
      if (!target || !target.matches) return;
      if (!target.matches('#baCurrSelector, select.bacurr')) return;
      handleChoice(target.value);
    },
    true
  );

  // Dropdown estilizado del widget (lista de <li class="currMovers">).
  document.addEventListener(
    'click',
    function (event) {
      if (!event.isTrusted) return;
      if (!event.target || !event.target.closest) return;
      var item = event.target.closest('li.currMovers');
      if (!item) return;
      var match = item.textContent.match(/\(([A-Z]{3})\)/);
      if (match) handleChoice(match[1]);
    },
    true
  );

  // Selector de país nativo del tema (drawer/footer): si el usuario cambia de
  // país por esta vía, esa es la nueva preferencia — no hay que pisarla.
  // El componente localization-form envía con `form.submit()`, que NO dispara
  // evento `submit`, así que se escucha el cambio del propio select.
  function onCountrySelect(event) {
    if (!event.isTrusted) return;
    var target = event.target;
    if (!target || !target.matches) return;
    if (!target.matches('select[name="country_code"]')) return;
    if (target.matches('#baCurrSelector, select.bacurr')) return; // por si el widget usara el mismo name

    var currency = countryCurrency[target.value];
    if (currency) {
      persistPreference(currency, target.value);
      clearDisplayCurrency();
      clearRetryGuard();
    }
  }

  document.addEventListener('input', onCountrySelect, true);
  document.addEventListener('change', onCountrySelect, true);

  // Cambio de idioma: sincroniza mercado/divisa con el idioma elegido.
  // Sustituye al antiguo script de theme.liquid que forzaba la divisa por
  // prefijo de URL en CADA carga (causa del bucle de recargas): aquí solo se
  // actúa ante una elección explícita del usuario, así que como mucho hay una
  // recarga por elección.
  // Registra la elección de idioma (preferencia de mercado + divisa de
  // cortesía) y devuelve el país de mercado correspondiente, o null si el
  // idioma no está mapeado.
  function registerLanguageChoice(lang) {
    var primary = String(lang || '').toLowerCase().split('-')[0];
    var display = languageDisplay[primary];
    var country = display ? marketMap[baseCurrency] : languageCountry[primary];

    if (display) {
      // Idioma cuya divisa no tiene mercado (inglés→GBP): mercado base con la
      // divisa pintada por Nova encima, igual que hacía el comportamiento
      // original. El checkout seguirá en la divisa base.
      persistDisplayCurrency(display);
    } else if (country) {
      clearDisplayCurrency();
    }

    if (!country || !(country in countryCurrency)) return null;

    persistPreference(countryCurrency[country], country);
    clearRetryGuard();
    return country;
  }

  // Selector de idioma del tema (drawer de localización). LocalizationForm
  // (global.js) envía el formulario completo en `input`; este listener va en
  // fase de captura, así que fija el país ANTES del envío y ambos cambios
  // viajan en el mismo submit.
  function onLanguageSelect(event) {
    if (!event.isTrusted) return;
    var target = event.target;
    if (!target || !target.matches) return;
    if (!target.matches('select[name="language_code"]')) return;

    var country = registerLanguageChoice(target.value);
    if (!country) return;

    var countrySelect = target.form && target.form.querySelector('select[name="country_code"]');
    if (countrySelect) {
      countrySelect.value = country;
    } else if (currentCountry() !== country) {
      postLocalization(country);
    }
  }

  document.addEventListener('input', onLanguageSelect, true);
  document.addEventListener('change', onLanguageSelect, true);

  // Selector de idioma de Weglot (el del header): es una lista de enlaces
  // (`.wg-li a`) que navegan a la URL con prefijo de idioma, sin pasar por el
  // formulario de localización del tema. Se escucha el clic real del usuario
  // sobre una opción (nunca sobre `.wgcurrent`, que solo abre el desplegable).
  //
  // ORDEN, no paralelismo: el POST de país tiene que llegar al servidor ANTES
  // que la navegación de Weglot. Lanzado en paralelo perdía la carrera: la
  // página aterrizaba con el mercado anterior (idioma nuevo con divisa vieja,
  // rescate posterior = recarga doble, y el país recién posteado no se veía
  // hasta la SIGUIENTE navegación — el "va una elección por detrás"). Aquí se
  // detiene la navegación, se confirma el país y se navega después. Detener a
  // Weglot es seguro (a diferencia de Nova): el idioma lo determina la URL de
  // destino, no su handler.
  document.addEventListener(
    'click',
    function (event) {
      if (!event.isTrusted) return;
      if (!event.target || !event.target.closest) return;

      var link = event.target.closest('.wg-li a, a[id^="weglot-language-"]');
      if (!link) return;

      var item = link.closest('.wg-li');
      if (item && item.classList.contains('wgcurrent')) return; // abre/cierra la lista, no es una elección

      var lang = (item && item.getAttribute('data-l')) || link.getAttribute('lang');
      var country = registerLanguageChoice(lang);
      if (!country) return; // idioma sin mapa: navegación normal de Weglot

      var href = link.getAttribute('href');
      if (!href || href === '#') return; // sin URL de destino no hay nada que ordenar
      if (currentCountry() === country) return; // el país ya es el correcto: navegación normal

      event.preventDefault();
      event.stopPropagation();

      var navigated = false;
      var go = function () {
        if (navigated) return;
        navigated = true;
        window.location.href = link.href;
      };

      // Red de seguridad: navegar aunque el POST tarde o falle (reconcile()
      // remataría con su único rescate en la siguiente carga).
      setTimeout(go, 2500);
      postLocalization(country).then(go);
    },
    true
  );

  /* ---------- Reconciliación al cargar (evita la condición de carrera) ---------- */

  // Reaplica la preferencia UNA sola vez por elección del usuario (el guard
  // solo lo limpia clearRetryGuard, en la propia elección). Si tampoco se
  // aplica (p. ej. una app de geolocalización la revierte), no insistir:
  // jamás un bucle de recargas.
  function attemptRescue(guardValue, country) {
    var attempted = null;
    try {
      attempted = window.sessionStorage.getItem(RETRY_KEY);
    } catch (error) {
      return; // sin sessionStorage no hay guard fiable: mejor no auto-recargar
    }

    if (attempted === guardValue) return;

    try {
      window.sessionStorage.setItem(RETRY_KEY, guardValue);
    } catch (error) {
      return;
    }

    switchMarket(country);
  }

  // País ancla de la preferencia: el que eligió el usuario si sigue siendo
  // válido para esa divisa; si no, el canónico del mapa divisa→país.
  function preferredCountry(pref) {
    if (pref.country && countryCurrency[pref.country] === pref.currency) return pref.country;
    return marketMap[pref.currency];
  }

  function reconcile() {
    var pref = readPreference();
    if (!pref || !pref.currency || !marketMap[pref.currency]) return;

    var active = window.Shopify && window.Shopify.currency && window.Shopify.currency.active;
    if (!active) return;

    // La divisa es la que el usuario quería. OJO: el guard NO se limpia aquí
    // (ver clearRetryGuard): limpiarlo en cada aterrizaje correcto permitía
    // un bucle infinito de recargas cuando otro actor devolvía la página a
    // la divisa base una y otra vez.
    if (active === pref.currency) {
      // Divisa correcta pero país desviado dentro del mismo mercado (varios
      // países comparten divisa): una cookie `localization` vieja o ajena
      // puede dejar, p. ej., país Bulgaria en el mercado EUR — y el checkout
      // preseleccionaría Bulgaria. Se restaura el país ancla, con el mismo
      // guard de un solo intento.
      var target = preferredCountry(pref);
      var country = currentCountry();
      if (target && country && country !== target) {
        attemptRescue(pref.currency + ':' + target, target);
      }
      return;
    }

    // Divisa distinta de la preferida: rescate con el guard de UN intento por
    // elección. Antes solo se rescataba desde la divisa base, pero un cambio
    // de idioma/mercado que pierde la carrera de cookies puede aterrizar en
    // CUALQUIER mercado anterior (pedir Polonia y caer en el mercado sueco):
    // la última elección explícita del usuario manda, venga de donde venga la
    // desviación. La preferencia solo la escriben elecciones reales
    // (isTrusted) y el guard hace imposible el bucle.
    var rescueCountry = preferredCountry(pref);
    if (rescueCountry) attemptRescue(pref.currency + ':' + rescueCountry, rescueCountry);
  }

  function boot() {
    buildMaps();
    reconcile();
    syncNovaWidget();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
